import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React from 'react';

import { selectedOptionPropTypes } from '../../../propTypes';
import { tourTransportDepartureOptionGroupPropTypes } from './PropTypes';

import formatPrice from '../../../util/formatPrice';

import {
  currencyDepositAndTotalPricePropTypes,
  findCurrencyItemFromListWithFallback,
} from '../../../util/currency';
import UpgradeCard from '../BookingFieldset/UpgradeCard';

const propTypes = exact({
  currencyPriceSetting: currencyDepositAndTotalPricePropTypes,
  currencySymbol: PropTypes.string.isRequired,
  groupIndex: PropTypes.number.isRequired,
  handleCardClick: PropTypes.func.isRequired,
  handleDropdownChange: PropTypes.func.isRequired,
  inboundArrivalText: PropTypes.string,
  inboundDepartureText: PropTypes.string,
  outboundArrivalText: PropTypes.string,
  outboundDepartureText: PropTypes.string,
  transportGroupOptions: PropTypes.arrayOf(tourTransportDepartureOptionGroupPropTypes).isRequired,
  selectedKeys: selectedOptionPropTypes.isRequired,
  selectedText: PropTypes.string.isRequired,
  soldOutText: PropTypes.string.isRequired,
  perPersonText: PropTypes.string,
});

const defaultProps = {
  perPersonText: 'pp',
};

const dropdownOptions = (options, currencyPriceSetting, currencySymbol, soldOutText) =>
  options.map((option) => {
    // This should take pax into consideration. All live sites currently only
    // using 1 pax type so this is fine for the time being
    const priceInCurrentCurrency = findCurrencyItemFromListWithFallback(
      option.pricePerPax[0].pricePerCurrency,
      currencyPriceSetting.currency
    )?.price;

    let text = option.option.outboundDepartureLocation;
    if (priceInCurrentCurrency && priceInCurrentCurrency !== 0)
      text += ` (+${currencySymbol}${formatPrice(priceInCurrentCurrency)})`;
    if (option.soldOut) text += ` (${soldOutText})`;
    if (!option.soldOut && option.limitedAvailability)
      text += ` (${option.limitedAvailabilityText})`;
    return { text, value: option.key, disabled: option.soldOut };
  });

function TransportGroup({
  groupIndex,
  handleCardClick,
  handleDropdownChange,
  currencyPriceSetting,
  currencySymbol,
  perPersonText,
  ...props
}) {
  const cards = props.transportGroupOptions;

  const cardSelectedDropdownOption = cards.map((option) => {
    const selectedCardDropdownOption = option.options.find(
      (tourTransportDepartureOption) =>
        tourTransportDepartureOption.key === props.selectedKeys.optionKey
    );

    return selectedCardDropdownOption || option.options[0]; // if selected option is not found use first dropdown option
  });

  // find selected optionKey within
  const selectedCard = cards.find((c) =>
    c.options.find(
      (tourTransportDepartureOption) =>
        tourTransportDepartureOption.key === props.selectedKeys.optionKey
    )
  );

  return (
    <div className="UpgradeGroup">
      <div className="UpgradeGroup_itemGroup">
        {cards.map((option, index) => (
          <div className="UpgradeGroup_item" key={option.key}>
            <UpgradeCard
              blockName="UpgradeGroup_card"
              currencySymbol={currencySymbol}
              inboundArrivalDate={cardSelectedDropdownOption[index].option.inboundArrivalDate}
              inboundArrivalText={props.inboundArrivalText}
              inboundArrivalTime={cardSelectedDropdownOption[index].option.inboundArrivalTime}
              inboundDepartureDate={cardSelectedDropdownOption[index].option.inboundDepartureDate}
              inboundDepartureText={props.inboundDepartureText}
              inboundDepartureTime={cardSelectedDropdownOption[index].option.inboundDepartureTime}
              description={cardSelectedDropdownOption[index].option.fullDescription}
              dropdownOptions={dropdownOptions(
                option.options,
                currencyPriceSetting,
                currencySymbol,
                props.soldOutText
              )}
              handleClick={() => handleCardClick(groupIndex, option.key, option.options[0].key)}
              handleDropdownChange={(event) =>
                handleDropdownChange(groupIndex, option.key, event, index)
              }
              isSelected={option.key === selectedCard?.key}
              limitedAvailability={option.limitedAvailability}
              limitedAvailabilityRemainingSpaces={option.limitedAvailabilityRemainingSpaces}
              limitedAvailabilityRemainingText={option.limitedAvailabilityText}
              limitedAvailabilityText={option.limitedAvailabilityText}
              outboundArrivalDate={cardSelectedDropdownOption[index].option.outboundArrivalDate}
              outboundArrivalText={props.inboundDepartureText}
              outboundArrivalTime={cardSelectedDropdownOption[index].option.outboundArrivalTime}
              outboundDepartureDate={cardSelectedDropdownOption[index].option.outboundDepartureDate}
              outboundDepartureText={props.inboundDepartureText}
              outboundDepartureTime={cardSelectedDropdownOption[index].option.outboundDepartureTime}
              perPersonText={perPersonText}
              selectedDropdownKey={cardSelectedDropdownOption[index].key}
              selectedText={props.selectedText}
              soldOut={option.soldOut || option.options.every((o) => o.soldOut)}
              soldOutText={props.soldOutText}
              title={option.cardTitle}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

TransportGroup.propTypes = propTypes;
TransportGroup.defaultProps = defaultProps;

export default TransportGroup;
